/**
 * API 服务 - 与后端数据库交互
 * 支持ACE CX QA Culture Rubric评价体系
 */

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';
export { API_BASE_URL };

/** ACE单项评分 */
export interface ACECriterionScore {
  id: number;
  name: string;
  score: number;
  passed: boolean;
  comment: string;
}

/** ACE类别评分 */
export interface ACECategoryScore {
  category: 'Accurate' | 'Customized' | 'Efficient';
  score: number;
  criteria: ACECriterionScore[];
}

export interface EvaluationRecord {
  id: number;
  filename: string;
  file_size: number | null;
  audio_duration: number | null;
  audio_file_path: string | null;
  transcription: string;
  detected_language: string | null;
  overall_score: number;
  
  // ACE评分
  accurate_score: number | null;
  customized_score: number | null;
  efficient_score: number | null;
  ace_scores: ACECategoryScore[] | null;
  ztp_violation: boolean | null;
  ztp_details: string | null;
  
  polite_phrases: string[];
  missing_polite_phrases: string[];
  rude_phrases: string[];
  summary: string;
  suggestions: string[];
  sentiment: string;
  dialogue_flow: string;
  timeline: Array<{
    speaker: 'agent' | 'customer';
    text: string;
    textCn: string;
    language: string;
    politePhrases: string[];
    rudePhrases: string[];
  }>;
  created_at: string;
}

export interface RecordsListResponse {
  total: number;
  records: EvaluationRecord[];
}

/**
 * 获取评估记录列表
 */
export async function getRecords(skip = 0, limit = 20): Promise<RecordsListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/records?skip=${skip}&limit=${limit}`);
  if (!response.ok) {
    throw new Error(`获取记录失败: ${response.statusText}`);
  }
  return response.json();
}

/**
 * 获取单条记录详情
 */
export async function getRecord(id: number): Promise<EvaluationRecord> {
  const response = await fetch(`${API_BASE_URL}/api/records/${id}`);
  if (!response.ok) {
    throw new Error(`获取记录详情失败: ${response.statusText}`);
  }
  return response.json();
}

/**
 * 删除记录
 */
export async function deleteRecord(id: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/records/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`删除记录失败: ${response.statusText}`);
  }
}

/**
 * 搜索记录
 */
export async function searchRecords(
  filename: string,
  skip = 0,
  limit = 20
): Promise<RecordsListResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/records/search/${encodeURIComponent(filename)}?skip=${skip}&limit=${limit}`
  );
  if (!response.ok) {
    throw new Error(`搜索记录失败: ${response.statusText}`);
  }
  return response.json();
}

/**
 * 流式分析回调类型
 */
export interface StreamCallbacks {
  onTranscribing?: () => void;
  onTranscribed?: (transcription: string, detectedLanguage?: string) => void;
  onAnalyzing?: () => void;
  onCompleted?: (analysis: any) => void;
  onError?: (error: string) => void;
}

/**
 * 上传并分析音频文件（流式响应）
 */
export async function analyzeAudioStream(
  file: File,
  callbacks: StreamCallbacks
): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/analyze-stream`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`分析失败: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new Error('无法读取响应流');
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6);
          if (!jsonStr || jsonStr.trim() === '') continue;

          try {
            const data = JSON.parse(jsonStr);

            if (data.error) {
              callbacks.onError?.(data.error);
              return;
            }

            switch (data.status) {
              case 'transcribing':
                callbacks.onTranscribing?.();
                break;
              case 'transcribed':
                callbacks.onTranscribed?.(data.transcription, data.detectedLanguage);
                break;
              case 'analyzing':
                callbacks.onAnalyzing?.();
                break;
              case 'completed':
                callbacks.onCompleted?.(data.analysis);
                break;
            }
          } catch (parseError) {
            console.error('JSON解析失败:', parseError);
            const jsonStart = jsonStr.indexOf('{');
            const jsonEnd = jsonStr.lastIndexOf('}');

            if (jsonStart >= 0 && jsonEnd >= 0 && jsonEnd > jsonStart) {
              try {
                const data = JSON.parse(jsonStr.substring(jsonStart, jsonEnd + 1));
                if (data.status === 'transcribed' && data.transcription) {
                  callbacks.onTranscribed?.(data.transcription, data.detectedLanguage);
                } else if (data.status === 'completed' && data.analysis) {
                  callbacks.onCompleted?.(data.analysis);
                }
              } catch (e) {
                callbacks.onError?.(`JSON解析错误: ${(parseError as Error).message}`);
              }
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
